export type Bilingual = {
  en: string
  fr: string
}

export const ecosystemsIntro = {
  eyebrow: { en: 'Our ecosystems', fr: 'Nos écosystèmes' },
  headlineLead: { en: 'One foundation.', fr: 'Une fondation.' },
  headlineAccent: {
    en: 'Multiple ecosystems.',
    fr: 'Plusieurs écosystèmes.',
  },
  intro: {
    en: 'Proven in municipal environments. Designed for organizations that connect people, services and systems.',
    fr: 'Éprouvée sur le terrain municipal. Pensée pour toutes les organisations qui relient des personnes, des services et des systèmes.',
  },
  context: {
    en: 'Same technology. Different contexts. More possibilities.',
    fr: 'Même technologie. Différents contextes. Plus de possibilités.',
  },
  indicators: [
    {
      icon: 'people' as const,
      label: { en: 'Better-served people', fr: 'Des personnes mieux servies' },
    },
    {
      icon: 'gear' as const,
      label: {
        en: 'More efficient operations',
        fr: 'Des opérations plus efficaces',
      },
    },
    {
      icon: 'chart' as const,
      label: {
        en: 'Better-informed decisions',
        fr: 'Des décisions plus éclairées',
      },
    },
  ],
}

export type Ecosystem = {
  id: string
  icon: 'city' | 'plane' | 'campus' | 'health' | 'telecom'
  image: string
  imageAlt: Bilingual
  title: Bilingual
  descriptors: Bilingual
  to: string
}

export const ecosystems: Ecosystem[] = [
  {
    id: 'cities',
    icon: 'city',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/fa40770c-f886-4551-b595-051f4c1aa207.jpg',
    imageAlt: {
      en: 'A town hall on a sunny summer day',
      fr: 'Un hôtel de ville par une journée d’été ensoleillée',
    },
    title: { en: 'Cities & government', fr: 'Villes et gouvernements' },
    descriptors: {
      en: 'Residents · Services · Territory',
      fr: 'Citoyens · Services · Territoire',
    },
    to: '/industries/cities-government',
  },
  {
    id: 'airports',
    icon: 'plane',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/94f02679-cf2b-4f3a-b63e-0e7ee01df783.jpg',
    imageAlt: {
      en: 'An aircraft at an airport gate at golden hour',
      fr: 'Un avion à la porte d’embarquement au coucher du soleil',
    },
    title: { en: 'Airports & transportation', fr: 'Aéroports et transport' },
    descriptors: {
      en: 'Travellers · Access · Mobility',
      fr: 'Voyageurs · Accès · Mobilité',
    },
    to: '/industries/airports',
  },
  {
    id: 'universities',
    icon: 'campus',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/9b96dc1d-893b-47ad-9271-d699a29fd07f.jpg',
    imageAlt: {
      en: 'Students walking on a university campus path',
      fr: 'Des étudiants marchant sur une allée de campus universitaire',
    },
    title: { en: 'Universities & campuses', fr: 'Universités et campus' },
    descriptors: {
      en: 'Students · Identity · Services',
      fr: 'Étudiants · Identité · Services',
    },
    to: '/industries/universities',
  },
  {
    id: 'health',
    icon: 'health',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/b00f236c-a750-4236-a854-54bfc6b959ad.jpg',
    imageAlt: {
      en: 'A healthcare professional speaking with a patient',
      fr: 'Une professionnelle de la santé discutant avec un patient',
    },
    title: { en: 'Healthcare networks', fr: 'Réseaux de santé' },
    descriptors: {
      en: 'Patients · Consent · Journeys',
      fr: 'Patients · Consentements · Parcours',
    },
    to: '/industries/health',
  },
  {
    id: 'telecom',
    icon: 'telecom',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/274365ad-717f-415a-9b53-4ee07c705e78.jpg',
    imageAlt: {
      en: 'A person using a smartphone in the city at night',
      fr: 'Une personne utilisant son téléphone en ville le soir',
    },
    title: { en: 'Telecom & media', fr: 'Télécom et médias' },
    descriptors: {
      en: 'Subscribers · Services · Communities',
      fr: 'Abonnés · Services · Communautés',
    },
    to: '/industries/telecom',
  },
]

export const ecosystemCta = {
  en: 'Explore the sector',
  fr: 'Explorer le secteur',
}

export const ecosystemsClosing = {
  eyebrow: { en: 'No limits', fr: 'Sans limite' },
  statement: [
    { en: 'Same foundation.', fr: 'Même fondation.' },
    { en: 'Different identities.', fr: 'Identités différentes.' },
    { en: 'Different ecosystems.', fr: 'Écosystèmes différents.' },
  ],
  statementAccent: {
    en: 'Multiple possibilities.',
    fr: 'Possibilités multiples.',
  },
  benefits: [
    {
      icon: 'people' as const,
      title: { en: 'Unified experience', fr: 'Expérience unifiée' },
      body: {
        en: 'One identity, multiple ways to interact.',
        fr: 'Une identité, plusieurs façons d’interagir.',
      },
    },
    {
      icon: 'gear' as const,
      title: { en: 'Operational efficiency', fr: 'Efficacité opérationnelle' },
      body: {
        en: 'Less complexity, more capacity.',
        fr: 'Moins de complexité, plus de capacité.',
      },
    },
    {
      icon: 'chart' as const,
      title: { en: 'Built to scale', fr: 'Conçue pour évoluer' },
      body: {
        en: 'A platform that evolves with your needs.',
        fr: 'Une plateforme qui évolue avec vos besoins.',
      },
    },
  ],
  cta: {
    en: 'Let’s talk about your sector',
    fr: 'Parlons de votre secteur',
  },
  footer: {
    en: 'More connected communities. More capable organizations.',
    fr: 'Des communautés plus connectées. Des organisations plus performantes.',
  },
}
